import { useCallback, useContext, useEffect, useState } from "preact/hooks";
import { ComponentChildren, createContext } from "preact";

import { ApiError } from "@api/base/api-caller";
import { fetchDataAccessTables, fetchTableDownloadUrl } from "@api/data-access";
import { usePopup } from "@contexts/popup";
import { DataAccessTable } from "@interfaces/data-access";
import { transformTableDownloadUrl, transformTables } from "@transformers/data-access";
import { trackError } from "@utils/analytics";

interface Meta {
  ready: boolean;
  tables: DataAccessTable[];
  download: (tableName: string) => Promise<void>;
}

interface ProviderProps {
  children: ComponentChildren;
}

const Context = createContext<Meta | null>(null);

export function DataAccessProvider({ children }: ProviderProps) {
  const { alert } = usePopup();
  const [ready, setReady] = useState(false);
  const [tables, setTables] = useState<DataAccessTable[]>([]);

  const download = useCallback(async (tableName: string) => {
    try {
      const url = transformTableDownloadUrl(
        await fetchTableDownloadUrl(tableName)
      );

      if (url) {
        window.open(url, "_blank");
      } else {
        throw new Error("Failed to get download URL");
      }
    } catch (error: ApiError | unknown) {
      if (error instanceof ApiError) {
        alert(error.message, "error");
        trackError(error);
      } else {
        alert("Error downloading table", "error");
      }
    }
  }, []);

  const load = useCallback(async () => {
    const tables = transformTables(
      await fetchDataAccessTables(),
    );

    setTables(tables);
    setReady(true);
  }, []);

  useEffect(() => {
    load();
  }, []);

  const value = {
    ready,
    tables,
    download,
  };

  return <Context.Provider value={value}>
    {children}
  </Context.Provider>
}

export function useDataAccess(): Meta {
  return useContext(Context)!;
}

